import { type FC, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../../../components/PageTitle";
import { Button } from "../../../../components/ui/button";
import {
  useContactApi,
  useCreateContactApi,
  useGroupsApi,
  useUpdateContactApi,
} from "../../../../hooks/use-distribution-api";

interface ContactFormData {
  companyName: string;
  contactName: string;
  email: string;
  notes: string;
  isActive: boolean;
  groupIds: string[];
}

export const ContactFormPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { data: existingContact, loading: loadingContact } = useContactApi({
    id: id || "",
    skip: !id,
  });
  const { data: groupsData } = useGroupsApi({ limit: 100 });
  const {
    createContact,
    loading: creating,
    error: createError,
  } = useCreateContactApi();
  const {
    updateContact,
    loading: updating,
    error: updateError,
  } = useUpdateContactApi();

  const [formData, setFormData] = useState<ContactFormData>({
    companyName: "",
    contactName: "",
    email: "",
    notes: "",
    isActive: true,
    groupIds: [],
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof ContactFormData, string>>
  >({});

  useEffect(() => {
    if (existingContact) {
      setFormData({
        companyName: existingContact.companyName,
        contactName: existingContact.contactName || "",
        email: existingContact.email,
        notes: existingContact.notes || "",
        isActive: existingContact.isActive,
        groupIds: existingContact.groups.map(g => g.id),
      });
    }
  }, [existingContact]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ContactFormData, string>> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "会社名は必須です";
    } else if (formData.companyName.length > 255) {
      newErrors.companyName = "会社名は255文字以内で入力してください";
    }

    if (!formData.email.trim()) {
      newErrors.email = "メールアドレスは必須です";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "有効なメールアドレスを入力してください";
    }

    if (formData.notes.length > 1000) {
      newErrors.notes = "備考は1000文字以内で入力してください";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGroupToggle = (groupId: string) => {
    setFormData(prev => ({
      ...prev,
      groupIds: prev.groupIds.includes(groupId)
        ? prev.groupIds.filter(id => id !== groupId)
        : [...prev.groupIds, groupId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const input = {
        companyName: formData.companyName,
        contactName: formData.contactName || undefined,
        email: formData.email,
        notes: formData.notes || undefined,
        isActive: formData.isActive,
        groupIds: formData.groupIds.length > 0 ? formData.groupIds : undefined,
      };

      if (isEdit && id) {
        await updateContact(id, input);
        navigate(`/admin/distribution/contacts/${id}`);
      } else {
        const created = await createContact(input);
        navigate(`/admin/distribution/contacts/${created.id}`);
      }
    } catch (_e) {
      // Error is handled by the hook
    }
  };

  const apiError = createError || updateError;
  const isLoading = creating || updating;

  if (isEdit && loadingContact) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>連絡先編集</PageTitle>
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center gap-4 mb-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/distribution/contacts")}
        >
          ← 戻る
        </Button>
        <PageTitle>{isEdit ? "連絡先編集" : "連絡先作成"}</PageTitle>
      </div>

      <div className="card bg-base-100 shadow-md max-w-2xl">
        <div className="card-body">
          {apiError && (
            <div className="alert alert-error mb-4">
              <span>{apiError.message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* 会社名 */}
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">
                  会社名 <span className="text-error">*</span>
                </span>
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    companyName: e.target.value,
                  }))
                }
                className={`input input-bordered ${
                  errors.companyName ? "input-error" : ""
                }`}
                placeholder="株式会社ABC"
              />
              {errors.companyName && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.companyName}
                  </span>
                </label>
              )}
            </div>

            {/* 担当者名 */}
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">担当者名</span>
              </label>
              <input
                type="text"
                value={formData.contactName}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    contactName: e.target.value,
                  }))
                }
                className="input input-bordered"
                placeholder="山田太郎"
              />
            </div>

            {/* メールアドレス */}
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">
                  メールアドレス <span className="text-error">*</span>
                </span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={e =>
                  setFormData(prev => ({ ...prev, email: e.target.value }))
                }
                className={`input input-bordered ${errors.email ? "input-error" : ""}`}
                placeholder="yamada@example.com"
              />
              {errors.email && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.email}
                  </span>
                </label>
              )}
            </div>

            {/* 所属グループ */}
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">所属グループ（複数選択可）</span>
              </label>
              {groupsData?.items.length === 0 ? (
                <div className="text-sm text-base-content/60">
                  グループがありません。
                  <Link
                    to="/admin/distribution/groups/new"
                    className="link link-primary"
                  >
                    グループを作成
                  </Link>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {groupsData?.items.map(group => (
                      <label
                        key={group.id}
                        className={`cursor-pointer badge badge-lg gap-2 ${
                          formData.groupIds.includes(group.id)
                            ? "badge-primary"
                            : "badge-outline"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.groupIds.includes(group.id)}
                          onChange={() => handleGroupToggle(group.id)}
                          className="hidden"
                        />
                        {group.isDefault && "★ "}
                        {group.name}
                      </label>
                    ))}
                  </div>
                  <div className="text-xs text-base-content/60 mt-2">
                    ※ チェックを外すとそのグループから除外されます
                  </div>
                </>
              )}
            </div>

            {/* 備考 */}
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">備考</span>
              </label>
              <textarea
                value={formData.notes}
                onChange={e =>
                  setFormData(prev => ({ ...prev, notes: e.target.value }))
                }
                className={`textarea textarea-bordered ${
                  errors.notes ? "textarea-error" : ""
                }`}
                placeholder="VIP顧客"
                rows={3}
              />
              {errors.notes && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.notes}
                  </span>
                </label>
              )}
            </div>

            {/* 有効フラグ */}
            <div className="form-control mb-6">
              <label className="label cursor-pointer justify-start gap-4">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      isActive: e.target.checked,
                    }))
                  }
                  className="checkbox checkbox-primary"
                />
                <span className="label-text">有効</span>
              </label>
            </div>

            {/* ボタン */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/admin/distribution/contacts")}
              >
                キャンセル
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : isEdit ? (
                  "更新"
                ) : (
                  "作成"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
